import { CommonModule } from '@angular/common';
import { Component, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { TabViewModule } from 'primeng/tabview';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AllLocationsService } from '../../services/all-locations.service';
import { PupCreditExportRequest } from '../../services/pup-credit-export.service';

@Component({
  selector: 'app-generate-pup-credit-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CalendarModule,
    CheckboxModule,
    DropdownModule,
    TabViewModule
  ],
  templateUrl: './generate-pup-credit-dialog.component.html'
})
export class GeneratePupCreditDialogComponent {
  months = [
    { name: 'January', key: 1 }, { name: 'February', key: 2 }, { name: 'March', key: 3 },
    { name: 'April', key: 4 }, { name: 'May', key: 5 }, { name: 'June', key: 6 },
    { name: 'July', key: 7 }, { name: 'August', key: 8 }, { name: 'September', key: 9 },
    { name: 'October', key: 10 }, { name: 'November', key: 11 }, { name: 'December', key: 12 }
  ];
  years: number[] = [];

  fromMonth: number;
  toMonth: number;
  year: number;
  includeLockerTotalBookings = false;
  separateDailyMonthly = true;
  errorMessage = '';

  selectedDate: Date = new Date();
  maxDate: Date = new Date();
  exportErrorMessage = '';

  selectedLocationName: string | null = null;
  excludeClosed = false;
  sortByCountry = true;

  // Reuse the All Locations dataset (terminals + lockers) via the service's
  // shared cache; if a page already loaded it, this costs no extra request.
  // Value is the location NAME (terminals and lockers use different id spaces).
  locationOptions = signal<{ label: string; value: string | null }[]>([
    { label: 'All locations', value: null }
  ]);

  constructor(
    protected ref: DynamicDialogRef,
    protected config: DynamicDialogConfig,
    private allLocationsService: AllLocationsService
  ) {
    this.allLocationsService.getLocations().subscribe({
      next: (locs) => {
        // The export matches by NAME, but a place can appear once as a terminal
        // and once as a locker (separate id-spaces) — same name twice. Collapse to
        // one option per name so the list isn't confusingly duplicated.
        const seen = new Set<string>();
        const names = locs
          .map((loc) => loc.name)
          .filter((name) => {
            const key = name?.trim();
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .sort((a, b) => a.localeCompare(b));

        this.locationOptions.set([
          { label: 'All locations', value: null },
          ...names.map((name) => ({ label: name, value: name }))
        ]);
      },
      error: () => { /* keep "All locations" only */ }
    });

    const now = new Date();
    this.fromMonth = now.getMonth() + 1;
    this.toMonth = now.getMonth() + 1;
    this.year = now.getFullYear();
    for (let y = 2020; y <= now.getFullYear(); y++) {
      this.years.push(y);
    }
  }

  // The dialog only collects the request and closes with it; the export itself
  // runs in PupCreditExportService so the work (and its toast) outlives the dialog.
  generate() {
    if (this.fromMonth > this.toMonth) {
      this.errorMessage = 'From month must be on or before To month.';
      return;
    }
    const request: PupCreditExportRequest = {
      kind: 'month',
      from: this.fromMonth,
      to: this.toMonth,
      year: this.year,
      includeLockerTotalBookings: this.includeLockerTotalBookings,
      locationName: this.selectedLocationName,
      excludeClosed: this.selectedLocationName == null && this.excludeClosed,
      sortByCountry: this.sortByCountry,
      separateDailyMonthly: this.separateDailyMonthly
    };
    this.ref.close(request);
  }

  exportAtDate() {
    if (!this.selectedDate) {
      this.exportErrorMessage = 'Please pick a date.';
      return;
    }
    const request: PupCreditExportRequest = {
      kind: 'date',
      date: this.formatDate(this.selectedDate),
      locationName: this.selectedLocationName,
      excludeClosed: this.selectedLocationName == null && this.excludeClosed,
      sortByCountry: this.sortByCountry
    };
    this.ref.close(request);
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  close() {
    this.ref.close();
  }
}
